import { FC, useCallback, useMemo, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { isEmpty } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { firmDailyNavSettingsUrl } from 'common/config/urls';
import { selectDailyNavApplyDates, selectDailyNavDayConfig } from 'common/constants/dailyNav';
import { useFirmScopedPermissions } from 'common/hooks';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { CustomScalarSpreadsheet, FormDialog, MessageBox } from 'components';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { useTertiaryNavigationRegistration } from 'context/FirmBaseLayoutContext';
import type { TertiaryNavigationActionControlConfig } from 'context/FirmBaseLayoutContext';
import { useGetDailyNavConfigApplyPreview } from 'services/hooks/dailyNav';
import {
  useFinalizeDailyNavDay,
  useGenerateDailyNavRecords,
  useGetCompanyListByFirm,
  useGetDailyNavPreviousValuations,
  useGetDailyNavRecords,
} from 'services/hooks/firms';
import { getArrayValue, getBooleanValue, getObjectValue, getStringValue } from 'utilities';
import DayConfigBanner from './components/DayConfigBanner';
import DeltaBreakdownDialog from './components/DeltaBreakdownDialog';
import FirmRulesIcon from './components/FirmRulesIcon';
import ReportDateSelector from './components/ReportDateSelector';
import ReviewPriceDialog from './components/ReviewPriceDialog';
import SecondaryBreakdownDialog from './components/SecondaryBreakdownDialog';
import {
  DAILY_NAV_REPORT_DATE_PARAM,
  dailyNavRecordNeedsReview,
  isValidStaffFinalizeMessage,
} from './dailyNav.constants';
import { DailyNavTableRow, useDailyNavSheet } from './hooks/useDailyNavSheet';

const DailyNav: FC = () => {
  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo, user } = getObjectValue(storeValue);
  const { id: firmId, slug: firmSlug } = getObjectValue(firmInfo);
  // Generating records is a staff-only tool. Reviewing a price mirrors the backend
  // DailyNavRecord update endpoint, which allows firm editors plus Scalar staff.
  const isStaffOrSuperuser = getBooleanValue(user?.is_staff) || getBooleanValue(user?.is_superuser);
  const { canMutateFirmScopedEntities } = useFirmScopedPermissions({ firmId });
  const canReviewPrice = canMutateFirmScopedEntities || isStaffOrSuperuser;

  const [reviewRow, setReviewRow] = useState<DailyNavTableRow | null>(null);
  const [breakdownRow, setBreakdownRow] = useState<DailyNavTableRow | null>(null);
  const [secondaryBreakdownRow, setSecondaryBreakdownRow] = useState<DailyNavTableRow | null>(null);
  const [isFinalizeConfirmOpen, setIsFinalizeConfirmOpen] = useState(false);
  const [staffFinalizeConfirmation, setStaffFinalizeConfirmation] = useState('');

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get(DAILY_NAV_REPORT_DATE_PARAM) ?? undefined;

  const setSelectedDate = useCallback(
    (date: string) => {
      setSearchParams(
        previous => {
          const next = new URLSearchParams(previous);
          next.set(DAILY_NAV_REPORT_DATE_PARAM, date);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const { mutate: generateDailyNavRecords, isPending: isGenerating } = useGenerateDailyNavRecords(firmId);
  const { mutateAsync: finalizeDailyNavDay, isPending: isFinalizing } = useFinalizeDailyNavDay(firmId);
  const { data: companies } = useGetCompanyListByFirm({ firmId });
  // With no report date selected, the backend resolves the current Eastern-time calendar day.
  const { data: dailyNavRecords, isLoading: isDailyNavRecordsLoading } = useGetDailyNavRecords({
    firmId,
    date: selectedDate,
  });

  // The records carry whichever date the backend settled on; it also scopes the Looking Glass
  // lookup and fills the Report date control before the user has picked anything.
  const resolvedDate = dailyNavRecords?.[0]?.date;
  const reportDate = selectedDate ?? resolvedDate;

  // The previous valuation per company: the backend returns the latest FINAL company
  // rollup strictly before the report date — one row per company — so the client no
  // longer loads the whole snapshot history to dedupe it. Fields must be explicit;
  // the endpoint's default set omits equity_value and percent change.
  const { data: previousValuations } = useGetDailyNavPreviousValuations({
    firmId,
    date: reportDate,
    fields: ['equity_value', 'percent_change_from_previous', 'common_share_value', 'cap_table_currency'],
    shouldQueryAutomatically: Boolean(firmId) && Boolean(reportDate),
  });

  // Which rules produced the rows on screen -- rides on the records, so no extra
  // request and it still answers when a breakdown came back null.
  //
  // "Is this the day in progress" comes from the preview, never from comparing the
  // report date to today: a date can BE today and already past its market's close.
  const { data: applyPreview } = useGetDailyNavConfigApplyPreview(firmId);
  const isOpenNavDay = useMemo(
    () =>
      Boolean(
        reportDate &&
          selectDailyNavApplyDates(applyPreview?.dates).applicableDates.some(date => date.nav_date === reportDate)
      ),
    [applyPreview?.dates, reportDate]
  );
  const { config: dayConfig, isStale, unanchoredCount } = useMemo(
    () => selectDailyNavDayConfig(dailyNavRecords, isOpenNavDay),
    [dailyNavRecords, isOpenNavDay]
  );

  const leftActionsNode = useMemo(
    () => (
      <Box display="flex" alignItems="center" gap={1}>
        <ReportDateSelector value={reportDate} onChange={setSelectedDate} />
        {/* Describes THAT day's rules: hover reads them, click opens the settings. */}
        <FirmRulesIcon config={dayConfig} settingsUrl={firmSlug ? firmDailyNavSettingsUrl(firmSlug) : undefined} />
      </Box>
    ),
    [reportDate, setSelectedDate, dayConfig, firmSlug]
  );

  // Generation is a daily cron, so the manual button is a fallback rather than the
  // normal path: shown only while the selected date has nothing, which covers a firm
  // just enabled, a backfill of an older date, and a day the run missed -- a daily
  // cadence cannot self-heal, so this is the recovery path until the next one.
  const canGenerateRecords = isStaffOrSuperuser && !isDailyNavRecordsLoading && isEmpty(dailyNavRecords);

  const finalizedRecord = dailyNavRecords?.find(record => Boolean(record.finalized_at));
  const isDayFinalized = Boolean(finalizedRecord?.finalized_at);
  const pendingReviewCount =
    dailyNavRecords?.filter(record => dailyNavRecordNeedsReview(record)).length ?? 0;
  const finalizeButtonLabel = isDayFinalized ? 'Finalized' : 'Finalize';
  const finalizedByName = getStringValue(finalizedRecord?.finalized_by_name);
  const finalizedAtLabel = finalizedRecord?.finalized_at
    ? format(parseISO(finalizedRecord.finalized_at), 'MMM d, yyyy, h:mm a')
    : '';
  const staffFinalizeMessage = getStringValue(finalizedRecord?.staff_finalize_confirmation);

  const canConfirmFinalize =
    pendingReviewCount === 0 && (!isStaffOrSuperuser || isValidStaffFinalizeMessage(staffFinalizeConfirmation));

  const closeFinalizeConfirm = useCallback(() => {
    setIsFinalizeConfirmOpen(false);
    setStaffFinalizeConfirmation('');
  }, []);

  const handleConfirmFinalize = useCallback(async () => {
    if (!canConfirmFinalize) {
      return;
    }
    await finalizeDailyNavDay({
      date: selectedDate,
      staff_finalize_confirmation: isStaffOrSuperuser ? staffFinalizeConfirmation.trim() : undefined,
    });
    closeFinalizeConfirm();
  }, [
    canConfirmFinalize,
    closeFinalizeConfirm,
    finalizeDailyNavDay,
    isStaffOrSuperuser,
    selectedDate,
    staffFinalizeConfirmation,
  ]);

  const tertiaryNavActions = useMemo<TertiaryNavigationActionControlConfig[]>(() => {
    const actions: TertiaryNavigationActionControlConfig[] = [];
    if (canReviewPrice || isDayFinalized) {
      actions.push({
        type: 'button',
        label: finalizeButtonLabel,
        variant: 'primary',
        disabled: isFinalizing || !firmId || isEmpty(dailyNavRecords),
        testId: 'finalize-daily-nav-button',
        onClick: () => setIsFinalizeConfirmOpen(true),
      });
    }
    if (canGenerateRecords) {
      actions.push({
        type: 'button',
        label: 'Generate Daily NAVs',
        variant: 'primary',
        disabled: isGenerating || !firmId,
        testId: 'generate-daily-nav-records-button',
        onClick: () => generateDailyNavRecords(selectedDate),
      });
    }
    return actions;
  }, [
    canGenerateRecords,
    canReviewPrice,
    dailyNavRecords,
    finalizeButtonLabel,
    firmId,
    generateDailyNavRecords,
    isDayFinalized,
    isFinalizing,
    isGenerating,
    selectedDate,
  ]);

  const tertiaryNavigation = useMemo(
    () => ({
      ariaLabel: 'Daily NAV controls',
      actions: tertiaryNavActions,
      leftActionsNode,
    }),
    [leftActionsNode, tertiaryNavActions]
  );

  useTertiaryNavigationRegistration(tertiaryNavigation);

  const { spreadsheet } = useDailyNavSheet({
    companies,
    firmSlug,
    records: dailyNavRecords,
    previousValuations,
    onReviewClick: setReviewRow,
    canReview: canReviewPrice,
    onBreakdownClick: setBreakdownRow,
    onSecondaryBreakdownClick: setSecondaryBreakdownRow,
  });

  const spreadsheets = useMemo(() => (spreadsheet?.rowConfig?.length ? [spreadsheet] : []), [spreadsheet]);
  const { onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const sheetProps = useMemo(() => {
    if (!spreadsheet) {
      return null;
    }
    return {
      ...spreadsheet,
      data: getArrayValue(spreadsheet.data),
      onChange,
      sheet: spreadsheet,
      workbook,
    } as unknown as typeof ScalarSpreadsheet;
  }, [spreadsheet, onChange, workbook]);

  return (
    // Scopes assets/scss/atoms/spreadsheets/_daily-nav.scss, which keeps cell
    // selection from changing the grid's geometry.
    <Box className="daily-nav-container">
      {!isDailyNavRecordsLoading && (
        <DayConfigBanner
          config={dayConfig}
          isStale={isStale}
          unanchoredCount={unanchoredCount}
          firmSettingsUrl={firmSlug ? firmDailyNavSettingsUrl(firmSlug) : undefined}
        />
      )}

      {isDailyNavRecordsLoading && <GridSkeleton maxColumns={10} maxRows={4} />}

      {!isDailyNavRecordsLoading && isEmpty(dailyNavRecords) && (
        <MessageBox
          fullWidth={false}
          title="Daily NAV"
          // "each trading day", not "each day": weekends and market holidays are not NAV
          // days, so an empty date is often correct rather than a missed run. Only staff
          // see the Generate action, so don't point other users at a button they lack.
          tagline={
            isStaffOrSuperuser
              ? 'No Daily NAV records yet for this date. Records are generated automatically each trading day; use Generate Daily NAVs to create them now.'
              : 'No Daily NAV records yet for this date. Records are generated automatically each trading day.'
          }
        />
      )}

      {sheetProps && (
        <WorkbookContext.Provider value={workbookContextValue}>
          <CustomScalarSpreadsheet {...sheetProps} isLoading={isDailyNavRecordsLoading} />
        </WorkbookContext.Provider>
      )}

      <FormDialog
        title={isDayFinalized ? 'Daily NAV Finalized' : 'Finalize Daily NAV'}
        open={isFinalizeConfirmOpen}
        editMode
        isValid={canConfirmFinalize}
        isLoading={isFinalizing}
        showSaveButton={!isDayFinalized}
        customButtonLabel="Finalize"
        saveButtonTestId="confirm-finalize-daily-nav-button"
        onSave={isDayFinalized ? undefined : handleConfirmFinalize}
        onClose={closeFinalizeConfirm}>
        <Box>
          {isDayFinalized ? (
            <>
              <Typography variant="body2" data-testid="daily-nav-finalized-by">
                Finalized by {finalizedByName || 'Unknown'}
              </Typography>
              {finalizedAtLabel && (
                <Typography variant="body2" mt={1} data-testid="daily-nav-finalized-at">
                  Finalized at {finalizedAtLabel}
                </Typography>
              )}
              {staffFinalizeMessage && (
                <Box mt={2}>
                  <Typography variant="body2">Confirmation message</Typography>
                  <Typography variant="body2" mt={0.5} data-testid="daily-nav-finalize-message">
                    {staffFinalizeMessage}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2">
                This associates each company&apos;s most recent finalized valuation with this Daily NAV day and records
                that you finalized it. This can only be done once. Every Review Price alert must be cleared first.
              </Typography>
              {pendingReviewCount > 0 && (
                <Typography variant="body2" color="error.main" mt={1} data-testid="daily-nav-finalize-pending-review">
                  {pendingReviewCount} {pendingReviewCount === 1 ? 'company still needs' : 'companies still need'}{' '}
                  review. Clear every red Review Price alert before you can finalize.
                </Typography>
              )}
              {isStaffOrSuperuser && (
                <Box mt={2}>
                  <Typography variant="body2">
                    You are signed in as Scalar staff. Enter a message explaining why you are authorized to finalize this
                    firm&apos;s Daily NAV.
                  </Typography>
                  <TextField
                    id="staff-finalize-confirmation"
                    data-testid="staff-finalize-confirmation-input"
                    fullWidth
                    margin="dense"
                    size="small"
                    multiline
                    minRows={3}
                    autoComplete="off"
                    label="Confirmation message"
                    slotProps={{ htmlInput: { maxLength: 1000 } }}
                    value={staffFinalizeConfirmation}
                    onChange={event => setStaffFinalizeConfirmation(event.target.value)}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </FormDialog>
      <ReviewPriceDialog row={reviewRow} open={Boolean(reviewRow)} onClose={() => setReviewRow(null)} />
      <DeltaBreakdownDialog row={breakdownRow} open={Boolean(breakdownRow)} onClose={() => setBreakdownRow(null)} />
      <SecondaryBreakdownDialog
        row={secondaryBreakdownRow}
        open={Boolean(secondaryBreakdownRow)}
        onClose={() => setSecondaryBreakdownRow(null)}
      />
    </Box>
  );
};

export default DailyNav;
